/**
 * Map Amplify v6 / Cognito error names to user-friendly copy. Cognito
 * surfaces native exception names like `NotAuthorizedException`; we
 * never want to render those raw strings to end users (both ugly and
 * leaks implementation detail).
 *
 * The defaultMessage covers any unhandled name so the user always sees
 * something rather than `undefined`.
 */
export function friendlyAuthError(err: unknown): string {
  const name =
    typeof err === "object" && err !== null && "name" in err
      ? String(err.name)
      : "";
  switch (name) {
    case "NotAuthorizedException":
      return "Wrong email or password.";
    case "UserNotFoundException":
      return "No account found for that email.";
    case "UserNotConfirmedException":
      return "Please confirm your email before signing in.";
    case "PasswordResetRequiredException":
      return "Your password needs to be reset. Contact support.";
    case "CodeMismatchException":
      return "That code doesn't match. Try again.";
    case "ExpiredCodeException":
      return "That code has expired. Request a new one.";
    case "InvalidPasswordException":
      return "Password doesn't meet the policy (min 14 chars, mixed case, number, symbol).";
    case "UsernameExistsException":
      return "An account already exists for that email.";
    case "LimitExceededException":
      return "Too many attempts. Please wait a few minutes and try again.";
    case "TooManyRequestsException":
      return "Too many requests. Please slow down.";
    case "TooManyFailedAttemptsException":
      return "Too many failed attempts. Account temporarily locked.";
    case "EnableSoftwareTokenMFAException":
      return "Couldn't enable TOTP. Try a different code.";
    case "SoftwareTokenMFANotFoundException":
      return "TOTP isn't set up yet on this account.";
    default: {
      const message =
        typeof err === "object" && err !== null && "message" in err
          ? String(err.message)
          : "Something went wrong. Please try again.";
      return message;
    }
  }
}
